import { useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Phone, ChevronRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { CTABand } from "../components/Shared";
import { BUSINESS, GOOGLE } from "../lib/data";
import { getArticle, GUIDE_LINKS } from "../lib/guides";

export default function ArticleGuide() {
  const { slug } = useParams();
  const article = getArticle(slug);

  const jsonLd = useMemo(() => {
    if (!article) return null;
    const url = `https://wisphomecleaning.ca/guides/${article.slug}`;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.metaTitle,
        description: article.description,
        image: `https://wisphomecleaning.ca${article.image}`,
        author: { "@type": "Organization", name: "Wisp Home Cleaning Services" },
        publisher: {
          "@type": "Organization",
          name: "Wisp Home Cleaning Services",
          logo: { "@type": "ImageObject", url: "https://wisphomecleaning.ca/wisp-logo.png" },
        },
        datePublished: "2026-07-22",
        dateModified: "2026-07-22",
        mainEntityOfPage: url,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: article.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
  }, [article]);

  if (!article) return <Navigate to="/guides/house-cleaning-cost-hamilton" replace />;

  const others = GUIDE_LINKS.filter((g) => g.slug !== article.slug);

  return (
    <>
      <SEO
        title={article.metaTitle}
        description={article.description}
        canonical={`/guides/${article.slug}`}
        jsonLd={jsonLd}
        image={article.image}
      />

      {/* Hero */}
      <section className="border-b border-slate-200 bg-white px-5 pt-10 pb-12 md:px-10">
        <div className="mx-auto max-w-4xl">
          <nav className="mb-6 flex items-center gap-1.5 text-sm text-slate-400" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-slate-600">Guides</span>
          </nav>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{article.overline}</span>
          <h1 className="mt-3 font-heading text-4xl font-light tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            {article.heading}
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">{article.lead}</p>
        </div>
      </section>

      {/* Cover image */}
      <section className="px-5 pt-10 md:px-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-4xl overflow-hidden rounded-3xl border border-slate-200 shadow-lg"
        >
          <img src={article.image} alt={article.heading} className="h-[280px] w-full object-cover md:h-[380px]" />
        </motion.div>
      </section>

      {/* Body */}
      <section className="px-5 py-14 md:px-10">
        <div className="mx-auto max-w-4xl space-y-10">
          {article.sections.map((s) => (
            <div key={s.heading}>
              <h2 className="font-heading text-2xl font-medium text-slate-900 md:text-3xl">{s.heading}</h2>
              {s.paras.map((p, i) => (
                <p key={i} className="mt-4 text-lg leading-relaxed text-slate-600">{p}</p>
              ))}
              {s.list && (
                <ul className="mt-5 space-y-3">
                  {s.list.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-700">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* CTA card */}
          <div className="rounded-3xl border border-slate-200 bg-muted p-8 md:p-10">
            <h2 className="font-heading text-2xl font-medium text-slate-900 md:text-3xl">Rather leave it to us?</h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              We'd be glad to help. Every quote starts with a free, no-obligation in-home visit, so your price is honest and tailored to your home.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a
                href={BUSINESS.phoneHref}
                data-testid="article-call-button"
                className="group flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 font-semibold text-primary-foreground transition-all duration-300 hover:-translate-y-0.5"
              >
                <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" /> Call or Text {BUSINESS.phone}
              </a>
              <Link
                to="/contact"
                data-testid="article-contact-button"
                className="flex items-center justify-center gap-2 rounded-full border-2 border-slate-200 px-7 py-3.5 font-semibold text-slate-700 transition-all duration-300 hover:border-primary hover:text-primary"
              >
                Book your free visit <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <p className="mt-4 text-sm text-slate-500">Rated {GOOGLE.rating}★ by {GOOGLE.count} Hamilton-area neighbours.</p>
          </div>

          {/* FAQ */}
          <div>
            <h2 className="font-heading text-2xl font-medium text-slate-900 md:text-3xl">Common questions</h2>
            <div className="mt-6 space-y-4">
              {article.faqs.map((f) => (
                <div key={f.q} data-testid="article-faq" className="rounded-2xl border border-slate-200 bg-white p-6">
                  <h3 className="font-heading text-lg font-semibold text-slate-900">{f.q}</h3>
                  <p className="mt-2 leading-relaxed text-slate-600">{f.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* More guides */}
          <div className="border-t border-slate-200 pt-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900">More helpful guides</h2>
            <div className="mt-4 flex flex-col gap-2.5">
              {others.map((g) => (
                <Link
                  key={g.slug}
                  to={g.path}
                  data-testid={`more-guide-${g.slug}`}
                  className="group inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                >
                  {g.title}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
